// This file handles 401/419 responses and redirects to login
// It's separated from router.tsx to avoid circular dependencies during HMR
// The router is accessed lazily via a getter function set after router creation

import { getErrorCauseChain } from '@/lib/error-cause-chain'
import { logoutActiveStripeConnectInstance } from '@/lib/stripe-connect-session'
import { isRecord } from '@/lib/utils'

type RouterWithHistory = { history?: { destroy: () => void } | null } | null | undefined

let getRouter: (() => RouterWithHistory) | null = null
const expectedSessionExpirationErrors = new WeakSet<object>()
let loggedOutRedirectStarted = false

const NON_REDIRECT_AUTH_PATHS = new Set([
  '/user',
  '/auth/status',
  '/login',
  '/logout',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/email/verification-notification',
  '/sanctum/csrf-cookie',
])

function shouldIgnoreLoggedOutRedirect(url: unknown) {
  return typeof url === 'string' && NON_REDIRECT_AUTH_PATHS.has(url)
}

function markExpectedSessionExpirationError(error: unknown) {
  if (error && (typeof error === 'object' || typeof error === 'function')) {
    expectedSessionExpirationErrors.add(error)
  }
}

export function isExpectedSessionExpirationError(error: unknown): boolean {
  return getErrorCauseChain(error).some((currentError) =>
    expectedSessionExpirationErrors.has(currentError)
  )
}

/**
 * True once a session-expiry redirect to /login has begun. The page is unloading, so anything
 * that fails after this point (for example TanStack's queued history flush calling pushState
 * on the destroyed history) is teardown noise, not a florist-facing problem.
 */
export function hasLoggedOutRedirectStarted(): boolean {
  return loggedOutRedirectStarted
}

export function setRouterGetter(getter: () => RouterWithHistory) {
  getRouter = getter
}

/**
 * Accepts Axios errors and the Axios-shaped `{ response, config }` objects built by upload clients.
 */
export const detectLoggedOutResponse = async (error: unknown): Promise<never> => {
  const response = isRecord(error) && isRecord(error.response) ? error.response : null
  const requestUrl = isRecord(error) && isRecord(error.config) ? error.config.url : undefined
  const isAuthUserRequest = requestUrl === '/user'

  // `/user` leaves navigation to the authenticated route guard, but still owns Stripe logout.
  if (
    response &&
    (response.status === 401 || response.status === 419) &&
    (isAuthUserRequest || !shouldIgnoreLoggedOutRedirect(requestUrl))
  ) {
    markExpectedSessionExpirationError(error)
    await logoutActiveStripeConnectInstance()

    if (!isAuthUserRequest) {
      console.log('logged out error, sending you to login page')
      const redirect = new URL(window.location.origin)
      redirect.pathname = '/login'
      redirect.searchParams.set('next', window.location.pathname + window.location.search)
      redirect.searchParams.set('reason', 'session-expired')
      loggedOutRedirectStarted = true

      // Make sure we don't get blocked by any beforeunload handlers
      if (getRouter) {
        try {
          const router = getRouter()
          if (router?.history) {
            router.history.destroy()
          }
        } catch (error) {
          console.error('Failed to destroy router', error)
        }
      }

      // This is a much safer navigation method for this situation.
      // Only the server knows the true login state now, and all local query/router cache is cleared.
      window.location.replace(redirect.href)
    }
  }

  throw error
}
